import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  XMarkIcon, ExclamationTriangleIcon, ClockIcon, CheckCircleIcon,
  ArrowPathIcon, XCircleIcon, InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { triggerBackfill, getBackfillStatus, getActiveBackfill, reconnectBackfill } from '../api/client';

// Handoff §1b + §5: supported log types, license tier, retention window, recommended chunk.
// `lookbackDays` is the per-type DEFAULT lookback cap applied by the runbook
// (v1.65.0) — GraphSignIn fetches the last 30 days, every other type caps at
// 7 days regardless of the outer date picker. Matches `$DefaultLookbackByLogType`
// in `runbook/M365Signal_Backfill-Tenant.ps1`; keep the two in sync.
const LOG_TYPE_CATALOG = [
  { key: 'GraphSignIn',         label: 'Graph Sign-Ins',          tier: 'Basic',    retentionDays: 30, lookbackDays: 30, chunkDays: 7,  api: 'Graph Beta auditLogs/signIns' },
  { key: 'DirectoryAudits',     label: 'Directory Audits',        tier: 'Basic',    retentionDays: 30, lookbackDays: 7,  chunkDays: 14, api: 'Graph v1.0 auditLogs/directoryAudits' },
  { key: 'MessageTrace',        label: 'Message Trace',           tier: 'Basic',    retentionDays: 90, lookbackDays: 7,  chunkDays: 7,  api: 'Graph Beta admin/exchange/tracing/messageTraces' },
  { key: 'ExchangeAuditLogs',   label: 'Exchange Audit',          tier: 'Basic',    retentionDays: 7,  lookbackDays: 7,  chunkDays: 1,  api: 'O365 Activity Feed Audit.Exchange' },
  { key: 'SharePointAuditLogs', label: 'SharePoint Audit',        tier: 'Basic',    retentionDays: 7,  lookbackDays: 7,  chunkDays: 1,  api: 'O365 Activity Feed Audit.SharePoint' },
  { key: 'GeneralAuditLogs',    label: 'General Audit',           tier: 'Basic',    retentionDays: 7,  lookbackDays: 7,  chunkDays: 1,  api: 'O365 Activity Feed Audit.General' },
  { key: 'DLPAuditLogs',        label: 'DLP Audit',               tier: 'Basic',    retentionDays: 7,  lookbackDays: 7,  chunkDays: 1,  api: 'O365 Activity Feed DLP.All' },
  { key: 'AADSignInLogs',       label: 'AAD Sign-Ins (Activity Feed)', tier: 'Basic', retentionDays: 7, lookbackDays: 7, chunkDays: 1, api: 'O365 Activity Feed Audit.AzureActiveDirectory' },
  { key: 'RiskDetections',      label: 'Risk Detections',         tier: 'Identity', retentionDays: 30, lookbackDays: 7,  chunkDays: 14, api: 'Graph v1.0 identityProtection/riskDetections' },
  { key: 'SecurityAlerts',      label: 'Security Alerts',         tier: 'Defender', retentionDays: 30, lookbackDays: 7,  chunkDays: 30, api: 'Graph v1.0 security/alerts_v2' },
  { key: 'SecurityIncidents',   label: 'Security Incidents',      tier: 'Defender', retentionDays: 30, lookbackDays: 7,  chunkDays: 30, api: 'Graph v1.0 security/incidents' },
  { key: 'UrlClickEvents',      label: 'URL Click Events',        tier: 'Defender', retentionDays: 30, lookbackDays: 7,  chunkDays: 7,  api: 'Advanced Hunting UrlClickEvents' },
];

const TIER_RANK = { '': 0, Basic: 1, Identity: 2, Defender: 3 };
const TERMINAL_STATUSES = new Set(['Completed', 'PartiallyCompleted', 'Failed', 'Stopped', 'Suspended']);

function toYyyyMmDd(d) {
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function defaultDateRange() {
  // Yesterday-UTC as endDate (handoff §1a — backfill API defaults endDate to yesterday)
  const now = new Date();
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 1));
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - 29); // 30-day window inclusive
  return { startDate: toYyyyMmDd(start), endDate: toYyyyMmDd(end) };
}

// Parse an upstream error string into { code, message } when possible. The
// runbook captures the response body verbatim from `Invoke-RestMethod`, which
// for the Activity Feed + Graph APIs is usually a JSON envelope shaped
// `{ "error": { "code": "...", "message": "..." } }` with embedded newlines.
// Falls back to returning the raw text so we never hide the underlying error.
function prettyError(err) {
  if (!err) return null;
  const raw = `${err}`.trim();
  try {
    const parsed = JSON.parse(raw);
    if (parsed && parsed.error && (parsed.error.message || parsed.error.code)) {
      return { code: parsed.error.code || null, message: parsed.error.message || raw };
    }
  } catch { /* not JSON — fall through */ }
  return { code: null, message: raw };
}

function statusBadgeClass(status) {
  if (status === 'Completed') return 'bg-emerald-900/40 text-emerald-300 border-emerald-700/50';
  if (status === 'Running' || status === 'Starting' || status === 'Queued') return 'bg-sky-900/40 text-sky-300 border-sky-700/50';
  if (status === 'Failed' || status === 'Stopped' || status === 'Suspended') return 'bg-rose-900/40 text-rose-300 border-rose-700/50';
  return 'bg-gray-800 text-gray-300 border-gray-700';
}

// Derive the per-logType display status for one row. The drawer iterates over the
// user-selected log types and overlays the backend's `perLogType` map — but a type
// can be MISSING from that map entirely (the backend returns `perLogType: {}` on a
// job that completed with no work for it) or carry 0 day-units. In those cases the
// row's day count is 0, which must NOT read as "queued" once the overall job is
// terminal — otherwise a finished backfill looks permanently stuck. Falls back to
// the backend's own per-type `state` when present, then the overall status.
// Returns one of: 'errored' | 'done' | 'running' | 'nodata' | 'queued'.
function deriveRowStatus(r, overallStatus, err) {
  if (err) return 'errored';
  const total     = r?.chunksTotal ?? 0;
  const completed = r?.chunksCompleted ?? 0;
  const records   = r?.recordsWritten ?? 0;
  const state     = (r?.state || '').toLowerCase();
  const overallTerminal = TERMINAL_STATUSES.has(overallStatus);

  if (total > 0 && completed >= total) return 'done';
  if (total > 0) return 'running';

  // total === 0 — no day-units reported (or no backend entry for this type at all)
  if (state.startsWith('complet') || state === 'done' || state === 'skipped') {
    return records > 0 ? 'done' : 'nodata';
  }
  if (state === 'running' || state === 'started' || state === 'inprogress') return 'running';
  if (overallTerminal) return 'nodata';   // job finished, nothing reported/written for this type
  return 'queued';
}

function sessionStorageKey(tenantId) {
  return `backfill:${tenantId}`;
}

export default function BackfillTenantDrawer({ tenant, onClose }) {
  const isOpen = !!tenant;

  const [{ startDate, endDate }, setRange] = useState(defaultDateRange);
  const [selected, setSelected] = useState(() => new Set());
  const [forceRecollect, setForceRecollect] = useState(false);  // re-pull days already collected (force=true&windowHours=24)
  const [confirming, setConfirming] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(null);

  // Progress / polling state (immediate-start branch)
  const [job, setJob] = useState(null);            // { jobId, ... } from triggerBackfill response
  const [statusResp, setStatusResp] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [lastPolledAt, setLastPolledAt] = useState(null);
  const [now, setNow] = useState(Date.now());      // ticked 1Hz so "X s ago" updates between 5s polls

  // Recovery for an enqueue that outran our HTTP timeout: the backend went on to
  // start the job, but this drawer never received its id. `timedOut` is set only
  // by the server's `enqueue_timeout` code -- never inferred from the message
  // text -- so the reconnect affordance appears exactly when it applies.
  const [timedOut, setTimedOut] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectNote, setReconnectNote] = useState(null);

  const pollRef = useRef(null);
  const tickRef = useRef(null);

  const licenseTier = tenant?.licenseTier || '';
  const tenantRank = TIER_RANK[licenseTier] ?? 0;

  // Build the displayable rows with tier-gating annotation
  const rows = useMemo(
    () => LOG_TYPE_CATALOG.map((r) => ({
      ...r,
      requiredRank: TIER_RANK[r.tier],
      allowed: tenantRank >= TIER_RANK[r.tier],
    })),
    [tenantRank]
  );

  // Reset state + seed defaults when the drawer opens for a new tenant
  useEffect(() => {
    if (!tenant) return;
    setRange(defaultDateRange());
    setForceRecollect(false);
    setConfirming(false);
    setStarting(false);
    setStartError(null);
    setStatusResp(null);
    setStatusError(null);
    setTimedOut(false);
    setReconnectNote(null);
    setReconnecting(false);

    // §5 template: preselect everything the tenant's tier allows
    const allowed = LOG_TYPE_CATALOG
      .filter((r) => tenantRank >= TIER_RANK[r.tier])
      .map((r) => r.key);
    setSelected(new Set(allowed));

    // Re-attach, fast path: the stash written by the tab that started the job.
    // It carries local extras the server does not (unitsQueued, forced), so it
    // wins when present.
    let stashedJob = null;
    try {
      const stashed = sessionStorage.getItem(sessionStorageKey(tenant.tenantId));
      if (stashed) {
        const parsed = JSON.parse(stashed);
        if (parsed && parsed.jobId) stashedJob = parsed;
      }
    } catch { stashedJob = null; }
    setJob(stashedJob);

    // Re-attach, authoritative path: ask the server what is running for this
    // tenant. sessionStorage is per-tab, so without this a second admin -- or the
    // same admin after a refresh -- sees an empty drawer while a backfill runs.
    // Only adopt it when we have nothing stashed, so we never trade the richer
    // local record for a thinner one describing the same job.
    let cancelled = false;
    (async () => {
      try {
        const resp = await getActiveBackfill(tenant.tenantId);
        if (cancelled || !resp?.active || !resp.backfill?.backfillId) return;
        setJob((current) => (current?.jobId ? current : {
          jobId: resp.backfill.backfillId,
          startDate: resp.backfill.startDate,
          endDate: resp.backfill.endDate,
          logTypes: resp.backfill.logTypes,
          requestedBy: resp.backfill.requestedBy,
          startedUtc: resp.backfill.createdUtc,
          unitsQueued: null,
          skippedAlreadyComplete: null,
          reattached: true,
        }));
      } catch {
        // Re-attach is a convenience, never a gate on opening the drawer.
      }
    })();
    return () => { cancelled = true; };
  }, [tenant, tenantRank]);

  const pollOnce = useCallback(async (tenantId, jobId) => {
    try {
      const resp = await getBackfillStatus(tenantId, jobId);
      setStatusResp(resp);
      setStatusError(null);
      setLastPolledAt(Date.now());
      if (resp && TERMINAL_STATUSES.has(resp.status)) {
        // Clear sessionStorage so the drawer doesn't keep re-attaching
        try { sessionStorage.removeItem(sessionStorageKey(tenantId)); } catch {}
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      }
    } catch (err) {
      setStatusError(err.message || String(err));
    }
  }, []);

  // Polling lifecycle — runs while job is set and drawer is open
  useEffect(() => {
    if (!isOpen || !job || !tenant) return undefined;
    // First poll immediately so the UI doesn't sit blank for 5s
    pollOnce(tenant.tenantId, job.jobId);
    pollRef.current = setInterval(() => pollOnce(tenant.tenantId, job.jobId), 5000);
    // 1Hz ticker drives the "updated Xs ago" relative-time display
    // between full poll cycles. Cleared on terminal status (see pollOnce)
    // and on unmount.
    tickRef.current = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    };
  }, [isOpen, job, tenant, pollOnce]);

  const toggleRow = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleStartClick = () => {
    setStartError(null);
    if (selected.size === 0) {
      setStartError('Select at least one log type to backfill.');
      return;
    }
    if (!startDate || !endDate) {
      setStartError('Pick a start date and end date.');
      return;
    }
    if (endDate < startDate) {
      setStartError('End date must be on or after start date.');
      return;
    }
    setConfirming(true);
  };

  const handleConfirmStart = async () => {
    setConfirming(false);
    setStarting(true);
    setStartError(null);
    try {
      const resp = await triggerBackfill(tenant.tenantId, {
        startDate, endDate,
        logTypes: Array.from(selected),
        force: forceRecollect || undefined,
        windowHours: forceRecollect ? 24 : undefined,
      });
      // Async backend (Option B) always queues immediately — there is no
      // scheduled/deferred path; enter the polling progress view directly.
      const newJob = {
        jobId: resp.jobId,
        portalJobId: resp.portalJobId,
        startDate: resp.startDate,
        endDate: resp.endDate,
        logTypes: resp.logTypes,
        startedUtc: resp.startedUtc,
        requestedBy: resp.requestedBy,
        // Backend's enqueue accounting — how many (logType, day) units were
        // actually queued vs skipped because they were already collected. Drives
        // the "already collected" banner so a 0-unit run isn't mistaken for a bug.
        unitsQueued: resp.unitsQueued ?? null,
        skippedAlreadyComplete: resp.skippedAlreadyComplete ?? null,
        forced: !!forceRecollect,
      };
      setJob(newJob);
      // Stash so the operator can re-attach by reopening the drawer
      try {
        sessionStorage.setItem(sessionStorageKey(tenant.tenantId), JSON.stringify(newJob));
      } catch {}
    } catch (err) {
      // A timeout is NOT "it failed" -- the backend is almost certainly still
      // starting the job. Keyed on the server's structured code, never on the
      // message text, so the wording can change without breaking recovery.
      setTimedOut(err?.body?.code === 'enqueue_timeout');
      setStartError(err.message || 'Failed to start backfill.');
    } finally {
      setStarting(false);
    }
  };

  // Recover a backfill whose id we never received. Deliberately operator-driven:
  // the backend cannot be asked "what is active" without attempting an enqueue,
  // and attempting one too early can start a second job -- hence the "wait about
  // a minute" copy and the explicit button rather than an automatic retry.
  const handleReconnect = async () => {
    setReconnecting(true);
    setReconnectNote(null);
    setStartError(null);
    try {
      const resp = await reconnectBackfill(tenant.tenantId, {
        startDate, endDate, logTypes: Array.from(selected),
      });
      const newJob = {
        jobId: resp.jobId,
        startDate, endDate,
        logTypes: Array.from(selected),
        unitsQueued: resp.unitsQueued ?? null,
        skippedAlreadyComplete: null,
        reattached: !!resp.recovered,
      };
      setJob(newJob);
      setTimedOut(false);
      setReconnectNote({ recovered: !!resp.recovered, message: resp.message });
      try {
        sessionStorage.setItem(sessionStorageKey(tenant.tenantId), JSON.stringify(newJob));
      } catch {}
    } catch (err) {
      setStartError(err.message || 'Could not reconnect to a running backfill.');
    } finally {
      setReconnecting(false);
    }
  };

  const handleClose = () => {
    // Closing the drawer does NOT cancel an in-flight backfill — it keeps
    // running on the runbook side and the operator can re-attach later.
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    onClose?.();
  };

  if (!isOpen) return null;

  const inProgressView = !!job;
  const setupView = !inProgressView;
  const selectedCount = selected.size;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={handleClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-[680px] bg-gray-900 border-l border-gray-800 shadow-2xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <ClockIcon className="h-5 w-5 text-sky-400" />
            <div>
              <h2 className="text-lg font-semibold text-gray-100">
                Backfill — {tenant.friendlyName || tenant.tenantId}
              </h2>
              <p className="text-xs text-gray-500 font-mono">{tenant.tenantId}</p>
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-200 transition-colors" title="Close">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {setupView && (
            <>
              {/* How-it-runs callout — under the async backend (Option B) the work
                  is queued and processed server-side at a paced rate, so there's no
                  longer a need to defer to off-peak hours. Replaces the old
                  "run after 5 PM Eastern" warning from the synchronous era. */}
              <div className="border border-sky-700/40 bg-sky-900/20 text-sky-200 text-sm rounded px-4 py-3 flex gap-3">
                <InformationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5 text-sky-400" />
                <div className="space-y-1">
                  <p className="font-medium">Backfill is queued and processed server-side.</p>
                  <p className="text-xs text-sky-300/80">
                    The collection backend paces the work itself, so it's safe to start anytime — it won't flood the client tenant's Microsoft APIs. Once it's queued you can close this drawer (or the tab); reopen Backfill for the same tenant to re-attach to progress.
                  </p>
                </div>
              </div>

              {/* Caveat banner */}
              <div className="border border-amber-700/50 bg-amber-900/20 text-amber-200 text-sm rounded px-4 py-3 flex gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-medium">Backfill is most useful within the first few days of onboarding a new tenant.</p>
                  <p className="text-xs text-amber-300/80">
                    Microsoft caps history at ~7 days for Activity Feed workloads and ~30 days for Entra audit/sign-in and Defender —
                    so days outside those windows return zero records. The function only writes blobs for days that don't already have one,
                    so re-running an overlapping window is safe.
                  </p>
                </div>
              </div>

              {/* License tier line */}
              <div className="text-xs text-gray-400">
                Tenant license tier:{' '}
                <span className="font-semibold text-gray-200">{licenseTier || '(not set)'}</span>
                {!licenseTier && (
                  <span className="ml-2 text-amber-400">— probe the tier before backfilling Identity/Defender types.</span>
                )}
              </div>

              {/* Date range */}
              <div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Start date (UTC)</label>
                    <input
                      type="date"
                      className="input-field w-full"
                      value={startDate}
                      onChange={(e) => setRange((r) => ({ ...r, startDate: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">End date (UTC)</label>
                    <input
                      type="date"
                      className="input-field w-full"
                      value={endDate}
                      onChange={(e) => setRange((r) => ({ ...r, endDate: e.target.value }))}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  This range is the <strong>ceiling</strong>. Each log type below caps to its own default lookback (30d for Graph Sign-Ins, 7d for everything else). Narrowing the range narrows all types.
                </p>
              </div>

              {/* Force re-collect — re-pull days that already have data. The backend
                  skips already-complete (logType, day) units by default; this makes
                  it re-fetch from source (force=true&windowHours=24). Needed to
                  repopulate days collected with bad/stale data. */}
              <div>
                <label className={
                  'flex items-start gap-3 px-3 py-2 rounded border cursor-pointer transition-colors ' +
                  (forceRecollect ? 'border-amber-700/50 bg-amber-900/20' : 'border-gray-800 hover:border-gray-700')
                }>
                  <input
                    type="checkbox"
                    checked={forceRecollect}
                    onChange={(e) => setForceRecollect(e.target.checked)}
                    className="mt-1 h-4 w-4"
                  />
                  <div>
                    <div className="text-sm text-gray-200">Force re-collect already-collected days</div>
                    <div className="text-xs text-gray-500">
                      By default the backfill skips days that already have data. Turn this on to re-fetch
                      them from Microsoft anyway — use it to repopulate days collected with bad or stale data.
                      Adds extra API load on the client tenant, so leave it off unless you're correcting a known gap.
                    </div>
                  </div>
                </label>
              </div>

              {/* Log type list */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Log types ({selectedCount} selected)</label>
                  <div className="text-xs text-gray-500 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setSelected(new Set(rows.filter((r) => r.allowed).map((r) => r.key)))}
                      className="text-sky-400 hover:text-sky-300"
                    >Select all (allowed)</button>
                    <button
                      type="button"
                      onClick={() => setSelected(new Set())}
                      className="text-gray-400 hover:text-gray-200"
                    >Clear</button>
                  </div>
                </div>
                <div className="border border-gray-800 rounded overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-800/40 text-xs uppercase text-gray-400">
                      <tr>
                        <th className="px-3 py-2 text-left w-8"></th>
                        <th className="px-3 py-2 text-left">Log type</th>
                        <th className="px-3 py-2 text-left">Tier</th>
                        <th className="px-3 py-2 text-right" title="Default per-type lookback. The outer date range is a ceiling; each type only fetches up to its lookback days even if the picker is wider.">Lookback</th>
                        <th className="px-3 py-2 text-right" title="Microsoft's retention window for this workload — older data is not available regardless of what we ask for.">Microsoft retention</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const isSelected = selected.has(r.key);
                        const disabled = !r.allowed;
                        return (
                          <tr
                            key={r.key}
                            className={
                              'border-t border-gray-800/60 ' +
                              (disabled ? 'opacity-50' : 'hover:bg-gray-800/30')
                            }
                            title={disabled ? `Requires ${r.tier === 'Identity' ? 'Identity+' : r.tier} license tier` : r.api}
                          >
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={disabled}
                                onChange={() => toggleRow(r.key)}
                                className="h-4 w-4"
                              />
                            </td>
                            <td className="px-3 py-2 text-gray-200">{r.label}</td>
                            <td className="px-3 py-2">
                              <span className={
                                'text-xs px-2 py-0.5 rounded border ' +
                                (r.tier === 'Basic' ? 'bg-gray-800 text-gray-300 border-gray-700'
                                  : r.tier === 'Identity' ? 'bg-purple-900/40 text-purple-300 border-purple-700/50'
                                  : 'bg-amber-900/40 text-amber-300 border-amber-700/50')
                              }>{r.tier}</span>
                            </td>
                            <td className="px-3 py-2 text-right text-xs text-gray-200 font-medium">{r.lookbackDays}d</td>
                            <td className="px-3 py-2 text-right text-xs text-gray-500">~{r.retentionDays}d</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {startError && (
                <div className={
                  timedOut
                    ? 'border border-amber-700/50 bg-amber-900/20 text-amber-200 text-sm rounded px-3 py-2'
                    : 'border border-rose-700/50 bg-rose-900/20 text-rose-200 text-sm rounded px-3 py-2'
                }>
                  {startError}
                </div>
              )}

              {/* A timed-out enqueue almost always means the backfill IS running
                  and we simply lost its id. Offer recovery rather than leaving
                  "try again" as the only move -- starting over would duplicate
                  the work and the operator has no way to see the first job. */}
              {timedOut && (
                <div className="border border-amber-700/50 bg-amber-950/30 rounded px-3 py-3 space-y-2">
                  <div className="text-xs text-amber-200/90">
                    Wait about a minute, then reconnect. If no backfill turns out to be
                    running, this starts one instead — days already collected are skipped.
                  </div>
                  <button
                    onClick={handleReconnect}
                    disabled={reconnecting}
                    className="btn-secondary text-sm inline-flex items-center gap-2 disabled:opacity-50"
                  >
                    <ArrowPathIcon className={`h-4 w-4 ${reconnecting ? 'animate-spin' : ''}`} />
                    {reconnecting ? 'Looking for a running backfill…' : 'Reconnect to a running backfill'}
                  </button>
                </div>
              )}
            </>
          )}

          {/* Progress view */}
          {inProgressView && (
            <>
              {/* Say plainly which of the two things reconnect did — "recovered"
                  and "started a new one" have very different implications. */}
              {reconnectNote && (
                <div className={
                  reconnectNote.recovered
                    ? 'border border-sky-700/50 bg-sky-900/20 text-sky-200 text-sm rounded px-3 py-2'
                    : 'border border-amber-700/50 bg-amber-900/20 text-amber-200 text-sm rounded px-3 py-2'
                }>
                  {reconnectNote.message}
                </div>
              )}

              {/* This drawer did not start the job — it attached to one already
                  running, so the enqueue accounting below is not available. */}
              {job?.reattached && !reconnectNote && (
                <div className="border border-sky-700/50 bg-sky-900/20 text-sky-200 text-sm rounded px-3 py-2">
                  Showing a backfill that was already running for this tenant
                  {job.requestedBy ? <> — started by <span className="font-mono text-xs">{job.requestedBy}</span></> : null}.
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`px-2.5 py-1 text-xs rounded border ${statusBadgeClass(statusResp?.status || 'Running')}`}>
                    {statusResp?.status || 'Running'}
                  </span>
                  {statusResp && (
                    <span className="text-xs text-gray-400">
                      {statusResp.totalRecords?.toLocaleString() || 0} records / {statusResp.totalBlobs?.toLocaleString() || 0} blobs
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  Job: <span className="font-mono text-gray-400">{job.jobId.slice(0, 8)}…</span>
                </div>
              </div>

              {/* Auto-refresh indicator — visible only while polling is active.
                  Most valuable during the early "New"/"Starting" window when the
                  table is all "queued" / "—" and could otherwise look frozen. */}
              {!TERMINAL_STATUSES.has(statusResp?.status) && (
                <div className="flex items-center gap-2 text-xs text-gray-500 -mt-2">
                  <ArrowPathIcon className="h-3.5 w-3.5 text-sky-400 animate-spin" />
                  <span>Auto-refreshing every 5s</span>
                  {lastPolledAt && (
                    <span className="text-gray-600">
                      · updated {Math.max(0, Math.floor((now - lastPolledAt) / 1000))}s ago
                    </span>
                  )}
                </div>
              )}

              {statusResp?.message && (
                <p className="text-sm text-gray-400">{statusResp.message}</p>
              )}

              {/* Enqueue accounting — explains a 0-record run. The backend reports
                  how many (logType, day) units were queued vs skipped because they
                  were already collected. An all-skipped run is the common "Completed
                  but nothing changed" case the operator would otherwise misread. */}
              {typeof job.skippedAlreadyComplete === 'number' && job.skippedAlreadyComplete > 0 && (
                job.unitsQueued === 0 ? (
                  <div className="border border-amber-700/50 bg-amber-900/20 text-amber-200 text-sm rounded px-4 py-3 flex gap-3">
                    <InformationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5 text-amber-400" />
                    <div className="space-y-1">
                      <p className="font-medium">
                        All {job.skippedAlreadyComplete} requested day{job.skippedAlreadyComplete === 1 ? '' : 's'} were already collected — nothing new was queued.
                      </p>
                      <p className="text-xs text-amber-300/80">
                        This isn't an error: the backfill skips days that already have data, so a re-run over an
                        already-collected window writes 0 records.
                        {!job.forced && ' To re-pull those days anyway (e.g. to fix bad or stale data), start over with “Force re-collect already-collected days” enabled.'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">
                    {job.unitsQueued ?? 0} day-unit{(job.unitsQueued ?? 0) === 1 ? '' : 's'} queued · {job.skippedAlreadyComplete} already collected (skipped)
                  </p>
                )
              )}

              {/* Banner reminding the operator they can close the drawer */}
              <div className="border border-sky-700/40 bg-sky-900/20 text-sky-200 text-xs rounded px-3 py-2 flex gap-2">
                <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  Backfill runs server-side. Closing this drawer (or the tab) does not cancel the job — reopen Backfill for the same tenant to re-attach to progress.
                </span>
              </div>

              {/* Per-logType rows */}
              <div className="border border-gray-800 rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-800/40 text-xs uppercase text-gray-400">
                    <tr>
                      <th className="px-3 py-2 text-left">Log type</th>
                      <th className="px-3 py-2 text-right">Days</th>
                      <th className="px-3 py-2 text-right">Records</th>
                      <th className="px-3 py-2 text-right">Blobs</th>
                      <th className="px-3 py-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(job.logTypes || []).map((lt) => {
                      const r = statusResp?.perLogType?.[lt];
                      const completed = r?.chunksCompleted ?? 0;
                      const total = r?.chunksTotal ?? 0;
                      const records = r?.recordsWritten ?? 0;
                      const blobs = r?.blobsWritten ?? 0;
                      const err = r?.error;
                      const rowStatus = deriveRowStatus(r, statusResp?.status, err);
                      const parsedErr = prettyError(err);
                      return (
                        <React.Fragment key={lt}>
                          <tr className="border-t border-gray-800/60">
                            <td className="px-3 py-2 text-gray-200">{lt}</td>
                            <td className="px-3 py-2 text-right text-gray-300 font-mono text-xs">
                              {total > 0 ? `${completed}/${total}` : '—'}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-300 font-mono text-xs">
                              {records.toLocaleString()}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-300 font-mono text-xs">
                              {blobs.toLocaleString()}
                            </td>
                            <td className="px-3 py-2">
                              {rowStatus === 'errored' ? (
                                <span className="text-rose-400 flex items-center gap-1.5 text-xs">
                                  <XCircleIcon className="h-4 w-4" /> errored
                                </span>
                              ) : rowStatus === 'done' ? (
                                <span className="text-emerald-400 flex items-center gap-1.5 text-xs">
                                  <CheckCircleIcon className="h-4 w-4" /> done
                                </span>
                              ) : rowStatus === 'running' ? (
                                <span className="text-sky-400 flex items-center gap-1.5 text-xs">
                                  <ArrowPathIcon className="h-4 w-4 animate-spin" /> running
                                </span>
                              ) : rowStatus === 'nodata' ? (
                                <span
                                  className="text-gray-400 flex items-center gap-1.5 text-xs"
                                  title="No new records were written for this log type. Most often the requested days were already collected (the backfill skips days that already have data), but it can also mean the window is outside Microsoft's retention. To force a re-pull of already-collected days, re-run with the force option."
                                >
                                  <InformationCircleIcon className="h-4 w-4" /> no new data
                                </span>
                              ) : (
                                <span className="text-gray-500 text-xs">queued</span>
                              )}
                            </td>
                          </tr>
                          {parsedErr && (
                            <tr className="border-t border-gray-800/30">
                              <td colSpan={5} className="px-3 pb-3">
                                <div className="border border-rose-700/40 bg-rose-900/15 rounded px-3 py-2 text-xs text-rose-200 whitespace-pre-wrap break-words font-mono">
                                  {parsedErr.code && (
                                    <span className="inline-block mr-2 px-1.5 py-0.5 rounded bg-rose-900/40 text-rose-300 border border-rose-700/40 not-italic">
                                      {parsedErr.code}
                                    </span>
                                  )}
                                  {parsedErr.message}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pipeline-stage clarifier — "done" here means the data was collected
                  to storage (Bronze). It still has to be processed into the searchable
                  analytics layer (Silver) by a separate Databricks pipeline, so it
                  won't appear in investigations the instant this says done. Shown once
                  the run is terminal so the operator doesn't expect instant searchability. */}
              {TERMINAL_STATUSES.has(statusResp?.status) && (
                <div className="flex items-start gap-2 text-xs text-gray-500">
                  <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Log collection done, but not searchable in investigations until full pipeline processing happens (typically 15min to 30 min later).
                  </span>
                </div>
              )}

              {/* Baseline build — final step. Waits for graph sign-in data to
                  land in silver, then fires the Tenant Baseline Backfill job
                  (fire-and-report; the ~60-min run continues on Databricks). */}
              {statusResp?.baseline && (() => {
                const b = statusResp.baseline;
                const waiting = b.phase === 'waiting-for-silver' && !b.triggered;
                const skipped = b.readinessMode === 'skipped';
                const errored = b.readinessMode === 'error';
                let tone, Icon, iconClass;
                if (errored)       { tone = 'border-rose-700/50 bg-rose-900/20 text-rose-200';     Icon = XCircleIcon;             iconClass = 'text-rose-400'; }
                else if (skipped)  { tone = 'border-gray-700 bg-gray-800/40 text-gray-400';         Icon = InformationCircleIcon;   iconClass = 'text-gray-500'; }
                else if (waiting)  { tone = 'border-sky-700/50 bg-sky-900/20 text-sky-200';          Icon = ArrowPathIcon;           iconClass = 'text-sky-400 animate-spin'; }
                else if (b.cappedEarly) { tone = 'border-amber-700/50 bg-amber-900/20 text-amber-200'; Icon = ExclamationTriangleIcon; iconClass = 'text-amber-400'; }
                else               { tone = 'border-emerald-700/50 bg-emerald-900/20 text-emerald-200'; Icon = CheckCircleIcon;       iconClass = 'text-emerald-400'; }
                return (
                  <div className={`border ${tone} rounded px-4 py-3 flex gap-3`}>
                    <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${iconClass}`} />
                    <div className="space-y-1 min-w-0">
                      <p className="font-medium text-sm">
                        {errored  ? 'Baseline build could not be triggered'
                          : skipped ? 'Baseline build skipped'
                          : waiting ? 'Waiting for sign-in data to reach the analytics layer…'
                          : b.readinessMode === 'no-data' ? 'Baseline build started (no sign-in data in window)'
                          : b.cappedEarly ? 'Baseline build started (data still settling)'
                          : 'Baseline build started'}
                      </p>
                      {waiting && (
                        <p className="text-xs opacity-80">
                          {b.daysCovered} day(s) with data · {(b.totalCount || 0).toLocaleString()} sign-in event(s) in silver
                          {b.stableStreak ? ` · stable ${b.stableStreak}/2 polls` : ''}
                        </p>
                      )}
                      {b.triggered && b.runId && (
                        <p className="text-xs opacity-80">
                          Databricks run <span className="font-mono">{b.runId}</span> — builds the 7-day &amp; 30-day baselines in the background (~60 min).
                        </p>
                      )}
                      {(b.message || b.error) && (
                        <p className="text-xs opacity-70 whitespace-pre-wrap break-words">{b.error || b.message}</p>
                      )}
                    </div>
                  </div>
                );
              })()}

              {statusError && (
                <div className="border border-rose-700/50 bg-rose-900/20 text-rose-200 text-sm rounded px-3 py-2">
                  Status poll failed: {statusError}
                </div>
              )}
              {statusResp?.error && (
                <div className="border border-rose-700/50 bg-rose-900/20 text-rose-200 text-sm rounded px-3 py-2 whitespace-pre-wrap">
                  Runbook error: {statusResp.error}
                </div>
              )}
            </>
          )}

        </div>

        {/* Footer */}
        <div className="border-t border-gray-800 px-6 py-4 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {setupView && `Window: ${startDate} → ${endDate}`}
            {inProgressView && statusResp?.startedUtc && (
              <>Started {new Date(statusResp.startedUtc).toLocaleString()}</>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleClose} className="btn-secondary text-sm px-4 py-2">
              {inProgressView && !TERMINAL_STATUSES.has(statusResp?.status) ? 'Close (keeps running)' : 'Close'}
            </button>
            {setupView && (
              <button
                onClick={handleStartClick}
                disabled={starting || selectedCount === 0}
                className="btn-primary text-sm px-4 py-2 flex items-center gap-2"
              >
                {starting ? (
                  <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Starting…</>
                ) : (
                  <>Start Backfill</>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation modal — rendered above the drawer */}
      {confirming && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70">
          <div className="bg-gray-900 border border-amber-700/50 rounded-lg shadow-2xl max-w-lg w-full mx-4 p-6 space-y-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-semibold text-amber-200 mb-2">
                  Backfill is only useful within the first few days of onboarding a new tenant.
                </h3>
                <p className="text-sm text-gray-300">
                  Microsoft retains most workload data for a limited window:
                </p>
                <ul className="text-sm text-gray-300 list-disc list-inside ml-2 mt-1 space-y-0.5">
                  <li>O365 Activity Feed (Exchange, SharePoint, DLP, General, AAD Sign-Ins): <strong>7 days</strong></li>
                  <li>Entra audit / sign-in / risk: <strong>~30 days</strong></li>
                  <li>Defender alerts &amp; incidents: <strong>~30 days</strong></li>
                  <li>Message Trace: <strong>~90 days</strong></li>
                </ul>
                <p className="text-sm text-gray-300 mt-2">
                  Running backfill more than a few days after a tenant was onboarded will return very little additional data —
                  the live workers have already collected most of what you'd want, and anything older than Microsoft's retention
                  is no longer available from their APIs.
                </p>
              </div>
            </div>

            <div className="border-t border-gray-800 pt-4 text-sm text-gray-200 space-y-1">
              <div>
                Continue with backfill of <strong>{selectedCount} log type{selectedCount === 1 ? '' : 's'}</strong>
                {' '}for <strong>{tenant.friendlyName || tenant.tenantId}</strong>
                {' '}({startDate} → {endDate})?
              </div>
              <div className="text-xs text-gray-400">
                Will be queued immediately and processed server-side.
              </div>
              {forceRecollect && (
                <div className="text-xs text-amber-300 flex items-start gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span><strong>Force re-collect is ON</strong> — days that already have data will be re-fetched from Microsoft, adding API load on the client tenant. Use only to correct a known data gap.</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => setConfirming(false)}
                className="btn-secondary text-sm px-4 py-2"
              >Cancel</button>
              <button
                onClick={handleConfirmStart}
                className="btn-primary text-sm px-4 py-2"
              >Yes, start backfill</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
